import type {
  FieldErrors,
  FieldPath,
  FieldValues,
  UseFormSetFocus,
} from "react-hook-form"

/**
 * Opt-in pattern for failed form Save: after field errors are set, call this so the
 * user is taken to the problem.
 *
 * - All field errors stay visible; only the first invalid field (document order) is
 *   scrolled into view and focused.
 * - Works inside scrollable dialog content (e.g. MUI `DialogContent`), not only the window.
 */
interface FocusFirstInvalidFieldParams<TFieldValues extends FieldValues> {
  errors: FieldErrors<TFieldValues>
  setFocus: UseFormSetFocus<TFieldValues>
}

const collectErrorFieldNames = (errors: FieldErrors, prefix = ""): string[] => {
  const names: string[] = []

  for (const [key, value] of Object.entries(errors)) {
    if (!value || typeof value !== "object") {
      continue
    }

    const path = prefix ? `${prefix}.${key}` : key

    if ("message" in value || "type" in value || "types" in value) {
      names.push(path)
      continue
    }

    names.push(...collectErrorFieldNames(value as FieldErrors, path))
  }

  return names
}

const resolveFieldElement = (fieldName: string): HTMLElement | null => {
  const byId = document.getElementById(fieldName)
  if (byId instanceof HTMLElement) {
    return byId
  }

  const byName = document.getElementsByName(fieldName)[0]
  if (byName instanceof HTMLElement) {
    return byName
  }

  return null
}

const compareDocumentPosition = (a: HTMLElement, b: HTMLElement): number => {
  const position = a.compareDocumentPosition(b)

  if (position & Node.DOCUMENT_POSITION_FOLLOWING) {
    return -1
  }

  if (position & Node.DOCUMENT_POSITION_PRECEDING) {
    return 1
  }

  return 0
}

/**
 * Scrolls to and focuses the first invalid field in document order.
 * Safe no-op when there are no errors or no resolvable field elements.
 * Defers focus until after paint to avoid the `setError` → `setFocus` race.
 */
export const focusFirstInvalidField = <TFieldValues extends FieldValues>({
  errors,
  setFocus,
}: FocusFirstInvalidFieldParams<TFieldValues>): void => {
  const fieldNames = collectErrorFieldNames(errors as FieldErrors)

  if (fieldNames.length === 0) {
    return
  }

  const elementsWithNames = fieldNames
    .map((name) => {
      const element = resolveFieldElement(name)
      return element ? { name: name as FieldPath<TFieldValues>, element } : null
    })
    .filter(
      (
        entry
      ): entry is { name: FieldPath<TFieldValues>; element: HTMLElement } =>
        Boolean(entry)
    )

  if (elementsWithNames.length === 0) {
    return
  }

  elementsWithNames.sort((a, b) =>
    compareDocumentPosition(a.element, b.element)
  )

  const first = elementsWithNames[0]

  // Defer past React/RHF paint so focus is not lost after setError.
  requestAnimationFrame(() => {
    const prefersReducedMotion =
      typeof window.matchMedia === "function" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches

    first.element.scrollIntoView({
      behavior: prefersReducedMotion ? "auto" : "smooth",
      block: "nearest",
    })
    // Prefer RHF setFocus so registered refs (Controller / FormTextField) work;
    // fall back to direct focus if needed.
    try {
      setFocus(first.name, { shouldSelect: true })
    } catch {
      first.element.focus({ preventScroll: true })
    }
  })
}
